<template>
  <div>
    <div class="d-flex justify-space-between align-center flex-wrap gap-2 mb-4">
      <div>
        <h6 class="ma-0 mb-1">Resumo do caixa</h6>
        <div class="text-caption text--secondary">
          {{ periodLabel }}
        </div>
      </div>
      <v-btn icon small @click="loadSummary" :loading="loading">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </div>

    <div class="d-flex align-center flex-wrap gap-2 mb-3">
      <v-text-field
        v-model="startDate"
        label="De"
        type="date"
        dense
        outlined
        hide-details
        style="max-width: 170px"
        @change="loadSummary"
      />
      <v-text-field
        v-model="endDate"
        label="Até"
        type="date"
        dense
        outlined
        hide-details
        style="max-width: 170px"
        @change="loadSummary"
      />
      <v-chip-group
        :value="activePreset"
        class="ma-0"
        @change="applyPreset"
      >
        <v-chip
          v-for="preset in presets"
          :key="preset.id"
          :value="preset.id"
          small
          label
          filter
          outlined
        >
          {{ preset.label }}
        </v-chip>
      </v-chip-group>
    </div>

    <v-alert v-if="error" type="error" dense text class="mb-3">
      {{ error }}
    </v-alert>

    <v-row dense class="mb-4">
      <v-col cols="6" sm="4" md>
        <v-card outlined class="pa-3">
          <div class="text-caption text--secondary">Entradas</div>
          <div class="text-h6 success--text">{{ summary.received | currency }}</div>
          <div class="text-caption text--secondary">recebidas</div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md>
        <v-card outlined class="pa-3">
          <div class="text-caption text--secondary">Saídas</div>
          <div class="text-h6 error--text">{{ summary.expenses | currency }}</div>
          <div class="text-caption text--secondary">pagas</div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md>
        <v-card outlined class="pa-3">
          <div class="text-caption text--secondary">Saldo</div>
          <div
            class="text-h6"
            :class="summary.balance >= 0 ? 'success--text' : 'error--text'"
          >
            {{ summary.balance | currency }}
          </div>
          <div class="text-caption text--secondary">entradas − saídas</div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md>
        <v-card outlined class="pa-3">
          <div class="text-caption text--secondary">Pendente</div>
          <div class="text-h6 warning--text">{{ summary.pending | currency }}</div>
          <div class="text-caption text--secondary">a receber</div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md>
        <v-card outlined class="pa-3">
          <div class="text-caption text--secondary">Faturado</div>
          <div class="text-h6">{{ summary.billed | currency }}</div>
          <div class="text-caption text--secondary">via faturas</div>
        </v-card>
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12" md="6">
        <v-card outlined class="pa-3 mb-3">
          <div class="d-flex justify-space-between align-center mb-3">
            <div class="font-weight-medium">Entradas por categoria</div>
            <span class="text-caption text--secondary">
              {{ incomeCategoryTotal | currency }}
            </span>
          </div>
          <div v-if="!byCategory.income.length" class="text-caption text--secondary">
            Nenhuma entrada no período
          </div>
          <div
            v-for="item in byCategory.income"
            :key="'in-' + item.category"
            class="mb-3"
          >
            <div class="d-flex justify-space-between text-caption mb-1">
              <span>{{ item.category }}</span>
              <span>
                {{ item.amount | currency }}
                <span class="text--secondary">({{ item.count }})</span>
              </span>
            </div>
            <v-progress-linear
              :value="categoryPercent(item.amount, incomeCategoryTotal)"
              color="success"
              height="6"
              rounded
            />
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card outlined class="pa-3 mb-3">
          <div class="d-flex justify-space-between align-center mb-3">
            <div class="font-weight-medium">Saídas por categoria</div>
            <span class="text-caption text--secondary">
              {{ expenseCategoryTotal | currency }}
            </span>
          </div>
          <div v-if="!byCategory.expense.length" class="text-caption text--secondary">
            Nenhuma saída no período
          </div>
          <div
            v-for="item in byCategory.expense"
            :key="'ex-' + item.category"
            class="mb-3"
          >
            <div class="d-flex justify-space-between text-caption mb-1">
              <span>{{ item.category }}</span>
              <span>
                {{ item.amount | currency }}
                <span class="text--secondary">({{ item.count }})</span>
              </span>
            </div>
            <v-progress-linear
              :value="categoryPercent(item.amount, expenseCategoryTotal)"
              color="error"
              height="6"
              rounded
            />
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-card outlined class="pa-3">
      <div class="d-flex justify-space-between align-center mb-2">
        <div class="font-weight-medium">Por cliente</div>
        <span class="text-caption text--secondary">
          {{ byCompany.length }} empresa(s)
        </span>
      </div>
      <v-data-table
        :loading="loading"
        :items="byCompany"
        :headers="companyHeaders"
        dense
        class="elevation-0"
        :items-per-page="10"
        no-data-text="Nenhum recebimento de cliente no período"
      >
        <template v-slot:item.name="{ item }">
          <base-avatar
            :seed="item.companyId"
            :size="20"
            avatarStyle="glass"
            color="secondary lighten-2"
          />
          <span class="ml-2">{{ item.name }}</span>
        </template>
        <template v-slot:item.expected="{ item }">
          {{ item.expected | currency }}
        </template>
        <template v-slot:item.received="{ item }">
          <span class="success--text">{{ item.received | currency }}</span>
        </template>
        <template v-slot:item.pending="{ item }">
          <span :class="item.pending ? 'warning--text' : 'text--secondary'">
            {{ item.pending | currency }}
          </span>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import moment from "moment";
import CASH from "@/services/ultimate/cash.js";

export default {
  name: "CashSummary",
  data() {
    return {
      loading: false,
      error: null,
      startDate: moment().startOf("month").format("YYYY-MM-DD"),
      endDate: moment().endOf("month").format("YYYY-MM-DD"),
      summary: {
        billed: 0,
        received: 0,
        pending: 0,
        expenses: 0,
        expensePending: 0,
        balance: 0,
      },
      byCategory: {
        income: [],
        expense: [],
      },
      byCompany: [],
      companyHeaders: [
        { text: "Cliente", value: "name" },
        { text: "Previsto", value: "expected" },
        { text: "Recebido", value: "received" },
        { text: "Pendente", value: "pending" },
        { text: "Lançamentos", value: "count" },
      ],
      presets: [
        { id: "current", label: "Este mês" },
        { id: "previous", label: "Mês passado" },
        { id: "last30", label: "Últimos 30 dias" },
      ],
    };
  },
  computed: {
    periodLabel() {
      return `${moment(this.startDate).format("DD/MM/YYYY")} — ${moment(
        this.endDate
      ).format("DD/MM/YYYY")}`;
    },
    incomeCategoryTotal() {
      return this.byCategory.income.reduce((sum, item) => sum + item.amount, 0);
    },
    expenseCategoryTotal() {
      return this.byCategory.expense.reduce((sum, item) => sum + item.amount, 0);
    },
    activePreset() {
      const start = this.startDate;
      const end = this.endDate;
      const currentStart = moment().startOf("month").format("YYYY-MM-DD");
      const currentEnd = moment().endOf("month").format("YYYY-MM-DD");
      if (start === currentStart && end === currentEnd) return "current";

      const prev = moment().subtract(1, "month");
      if (
        start === prev.clone().startOf("month").format("YYYY-MM-DD") &&
        end === prev.clone().endOf("month").format("YYYY-MM-DD")
      ) {
        return "previous";
      }

      const last30Start = moment().subtract(29, "days").format("YYYY-MM-DD");
      const today = moment().format("YYYY-MM-DD");
      if (start === last30Start && end === today) return "last30";

      return null;
    },
  },
  methods: {
    categoryPercent(amount, total) {
      if (!total) return 0;
      return Math.round((amount / total) * 100);
    },
    applyPreset(presetId) {
      if (presetId === "current") {
        this.startDate = moment().startOf("month").format("YYYY-MM-DD");
        this.endDate = moment().endOf("month").format("YYYY-MM-DD");
      } else if (presetId === "previous") {
        const prev = moment().subtract(1, "month");
        this.startDate = prev.clone().startOf("month").format("YYYY-MM-DD");
        this.endDate = prev.clone().endOf("month").format("YYYY-MM-DD");
      } else if (presetId === "last30") {
        this.startDate = moment().subtract(29, "days").format("YYYY-MM-DD");
        this.endDate = moment().format("YYYY-MM-DD");
      }
      this.loadSummary();
    },
    async loadSummary() {
      try {
        this.loading = true;
        this.error = null;
        const response = await CASH.getSummary({
          startDate: this.startDate,
          endDate: this.endDate,
        });
        this.summary = response.summary || this.summary;
        this.byCategory = response.byCategory || { income: [], expense: [] };
        this.byCompany = response.byCompany || [];
      } catch (error) {
        this.error = error.message || "Erro ao carregar resumo";
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.loadSummary();
  },
};
</script>
